import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, euro, LOGO_URL, statusMeta } from "../lib/api";
import { toast } from "sonner";
import { LeadDetail, dealStatusCls, payCls } from "../components/LeadDetail";
import {
  LayoutGrid, Car, Users, CreditCard, Bell, Settings, LogOut, Plus, Trash2, Pencil, X, Loader2, RefreshCw, BarChart3,
} from "lucide-react";
import { AdminAnalytics } from "../components/AdminAnalytics";
import { AccountSettings } from "../components/AccountSettings";

const NAV = [
  { id: "overview", label: "Panoramica", icon: LayoutGrid },
  { id: "analytics", label: "Statistiche", icon: BarChart3 },
  { id: "vehicles", label: "Vetture", icon: Car },
  { id: "leads", label: "Clienti", icon: Users },
  { id: "payments", label: "Pagamenti", icon: CreditCard },
  { id: "notifications", label: "Notifiche", icon: Bell },
  { id: "config", label: "Percorso", icon: Settings },
];

const STATUSES = ["disponibile", "in_trattativa", "temporaneamente_bloccata", "venduta"];

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState(null); // vehicle obj or null
  const [openLead, setOpenLead] = useState(null);
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const runSync = async () => {
    setSyncing(true);
    try {
      await api.post("/admin/sync");
      toast.info("Sincronizzazione avviata, attendi qualche istante…");
      const started = Date.now();
      const poll = async () => {
        const { data } = await api.get("/admin/sync");
        if (data.running && Date.now() - started < 180000) return setTimeout(poll, 3000);
        setSync(data); setSyncing(false); loadAll();
        if (data.last_error) toast.error(`Sincronizzazione fallita: ${data.last_error}`);
        else if (data.last_sync) toast.success(`Sincronizzate ${data.last_sync.feed_count} vetture (${data.last_sync.created} nuove, ${data.last_sync.updated} aggiornate, ${data.last_sync.marked_sold} vendute)`);
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Sincronizzazione fallita"); setSyncing(false); }
  };
  const publishSync = async () => {
    if (!window.confirm("Pubblicare il parco auto del gestionale e rimuovere le vetture demo?")) return;
    await api.post("/admin/sync/publish"); toast.success("Parco auto pubblicato"); loadAll();
  };

  useEffect(() => { if (checked && !user) nav("/admin/login"); }, [checked, user, nav]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/vehicles").then((r) => setVehicles(r.data));
    api.get("/admin/sync").then((r) => setSync(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data));
    api.get("/admin/payments").then((r) => setPayments(r.data));
    api.get("/admin/notifications").then((r) => setNotes(r.data));
    api.get("/config").then((r) => setConfig(r.data));
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  useEffect(() => {
    if (tab === "notifications") api.post("/admin/notifications/read").then(loadNotes);
  }, [tab]);
  const loadNotes = () => api.get("/admin/notifications").then((r) => setNotes(r.data));

  const unread = notes.filter((n) => !n.read).length;

  const saveVehicle = async (data) => {
    try {
      if (data.id) await api.put(`/admin/vehicles/${data.id}`, data);
      else await api.post("/admin/vehicles", data);
      toast.success("Vettura salvata");
      setEditing(null); loadAll();
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const delVehicle = async (id) => {
    if (!window.confirm("Eliminare questa vettura?")) return;
    await api.delete(`/admin/vehicles/${id}`); toast.success("Eliminata"); loadAll();
  };
  const setStatus = async (id, status) => { await api.put(`/admin/vehicles/${id}`, { status }); loadAll(); };
  const saveConfig = async () => { await api.put("/config", config); toast.success("Percorso aggiornato"); };

  if (!user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-16 md:w-60 bg-ink text-secondary flex flex-col shrink-0">
        <div className="h-20 flex items-center justify-center md:justify-start md:px-6 border-b border-white/10">
          <div className="bg-white rounded-lg px-2 py-1">
            <img src={LOGO_URL} alt="" className="h-7 w-auto object-contain" />
          </div>
        </div>
        <nav className="flex-1 p-2 md:p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => setTab(n.id)}
              className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 rounded-xl text-sm transition-colors ${tab === n.id ? "bg-white/10 text-white" : "text-secondary/60 hover:text-white"}`}>
              <n.icon size={18} strokeWidth={1.5} />
              <span className="hidden md:inline">{n.label}</span>
              {n.id === "notifications" && unread > 0 && <span className="ml-auto text-[10px] bg-clay text-white rounded-full px-1.5 hidden md:inline">{unread}</span>}
            </button>
          ))}
        </nav>
        <button data-testid="logout-btn" onClick={() => logout().then(() => nav("/admin/login"))}
          className="m-3 flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-secondary/60 hover:text-white transition-colors">
          <LogOut size={18} strokeWidth={1.5} /><span className="hidden md:inline">Esci</span>
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto h-screen">
        <div className="p-5 md:p-10 max-w-6xl">
          <h1 className="font-serif text-4xl font-light mb-8 capitalize">{NAV.find((n) => n.id === tab)?.label}</h1>

          {tab === "overview" && stats && (
            <div className="space-y-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  ["Vetture", stats.total_vehicles], ["Visite", stats.total_views],
                  ["Preferiti", stats.total_favorites], ["Percorsi avviati", stats.total_leads],
                  ["Preventivi", stats.quotes], ["Appuntamenti", stats.appointments],
                  ["Blocchi", stats.blocks], ["Depositi pagati", stats.paid_deposits],
                ].map(([k, val], i) => (
                  <div key={i} className="p-5 bg-white border border-border rounded-2xl">
                    <div className="text-xs uppercase tracking-wider text-ink-soft">{k}</div>
                    <div className="font-serif text-4xl mt-1">{val}</div>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-border rounded-2xl p-6">
                <h3 className="font-serif text-2xl mb-4">Funnel del percorso</h3>
                {Object.entries(stats.funnel).map(([ph, count]) => {
                  const max = Math.max(...Object.values(stats.funnel), 1);
                  return (
                    <div key={ph} className="flex items-center gap-3 mb-2">
                      <div className="w-40 text-sm text-ink-soft capitalize">{ph}</div>
                      <div className="flex-1 h-6 bg-secondary rounded-lg overflow-hidden">
                        <div className="h-full bg-ink rounded-lg" style={{ width: `${(count / max) * 100}%` }} />
                      </div>
                      <div className="w-8 text-sm text-right">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "analytics" && <AdminAnalytics />}

          {tab === "vehicles" && (
            <div>
              <div data-testid="sync-panel" className="bg-ink text-white rounded-2xl p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/60"><RefreshCw size={13} /> Gestionale auto · sync automatica ogni ora</div>
                  <div data-testid="sync-status" className="text-sm mt-1.5">
                    {sync?.last_sync ? `Ultima sincronizzazione ${new Date(sync.last_sync.at).toLocaleString("it-IT")} · ${sync.last_sync.feed_count} vetture nel feed · ${sync.last_sync.created} nuove · ${sync.last_sync.updated} aggiornate · ${sync.last_sync.marked_sold} vendute${sync.last_sync.images_missing ? ` · ${sync.last_sync.images_missing} senza foto (immagine provvisoria)` : ""}` : "Nessuna sincronizzazione eseguita"}
                  </div>
                  {sync && !sync.published && sync.imported_count > 0 && (
                    <div data-testid="sync-preview-note" className="text-xs mt-2 text-amber-300">Anteprima: {sync.imported_count} vetture importate NON ancora visibili al pubblico. Il sito mostra ancora le {sync.demo_count} vetture demo.</div>
                  )}
                  {sync?.published && <div className="text-xs mt-2 text-[#68b39d]">Parco auto del gestionale pubblicato</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid="sync-now-btn" onClick={runSync} disabled={syncing}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-ink text-sm hover:opacity-90 disabled:opacity-50">
                    {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Sincronizza ora
                  </button>
                  {sync && !sync.published && sync.imported_count > 0 && (
                    <button data-testid="sync-publish-btn" onClick={publishSync}
                      className="px-5 py-3 rounded-full bg-[#68b39d] text-ink text-sm hover:opacity-90">Pubblica</button>
                  )}
                </div>
              </div>
              <button data-testid="add-vehicle-btn" onClick={() => setEditing({})}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ink text-white text-sm mb-6 hover:opacity-90 transition-opacity">
                <Plus size={16} /> Aggiungi vettura
              </button>
              <div className="text-xs text-ink-soft mb-3">{vehicles.length} vetture in archivio</div>
              <div className="space-y-3">
                {vehicles.map((v) => (
                  <div key={v.id} data-testid={`admin-vehicle-${v.id}`} className="flex items-center gap-4 p-3 bg-white border border-border rounded-2xl">
                    <img src={v.images?.[0] || "/placeholder-car.jpg"} onError={(e) => { e.currentTarget.src = "/placeholder-car.jpg"; }} alt="" className="w-20 h-16 object-cover rounded-xl bg-secondary" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{v.make} {v.model} <span className="text-ink-soft font-normal">{v.version}</span></div>
                      <div className="text-sm text-ink-soft flex flex-wrap gap-x-2">
                        <span>{euro(v.price)} · {v.year} · {Number(v.km).toLocaleString("it-IT")} km</span>
                        {v.source === "gestionaleauto" && <span className="px-1.5 rounded bg-secondary text-[10px] uppercase tracking-wider self-center">Gestionale</span>}
                        {!v.source && <span className="px-1.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider self-center">Demo</span>}
                        {v.source === "gestionaleauto" && !v.monthly_rate && <span className="text-[10px] uppercase tracking-wider text-clay self-center">Rata da inserire</span>}
                        {v.images_missing && <span data-testid={`images-missing-${v.id}`} className="px-1.5 rounded bg-clay/10 text-clay text-[10px] uppercase tracking-wider self-center">Foto non disponibili dal gestionale</span>}
                      </div>
                      <a href={`/vettura/${v.id}`} target="_blank" rel="noreferrer" data-testid={`preview-vehicle-${v.id}`} className="text-xs underline underline-offset-2 text-ink-soft">Anteprima scheda</a>
                    </div>
                    <select value={v.status} data-testid={`status-select-${v.id}`} onChange={(e) => setStatus(v.id, e.target.value)}
                      className="h-9 px-2 rounded-lg border border-border text-xs bg-white">
                      {STATUSES.map((s) => <option key={s} value={s}>{statusMeta(s).label}</option>)}
                    </select>
                    <button data-testid={`edit-vehicle-${v.id}`} onClick={() => setEditing(v)} className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-secondary"><Pencil size={15} /></button>
                    <button data-testid={`delete-vehicle-${v.id}`} onClick={() => delVehicle(v.id)} className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-clay/10 text-clay"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "leads" && (
            <div className="bg-white border border-border rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-ink-soft"><tr>
                  <th className="text-left p-3 font-medium">Cliente</th><th className="text-left p-3 font-medium">Vettura</th>
                  <th className="text-left p-3 font-medium">Fase</th><th className="text-left p-3 font-medium hidden md:table-cell">Contatto</th>
                  <th className="text-left p-3 font-medium">Trattativa</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Aggiornato</th>
                </tr></thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setOpenLead(l.id)} tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && setOpenLead(l.id)}
                      className="border-t border-border cursor-pointer hover:bg-secondary/60 transition-colors">
                      <td className="p-3 font-medium underline decoration-border underline-offset-4">{l.customer?.name || <span className="text-ink-soft font-normal no-underline">Senza nome</span>}</td>
                      <td className="p-3">{l.vehicle_label}</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-secondary text-xs">{l.phase_label}</span></td>
                      <td className="p-3 hidden md:table-cell">{l.customer?.phone || "—"}</td>
                      <td className="p-3"><span data-testid={`lead-deal-${l.id}`} className={`px-2 py-0.5 rounded-full text-xs ${dealStatusCls(l.deal_status)}`}>{l.deal_status || "Nuovo"}</span></td>
                      <td className="p-3 text-ink-soft text-xs hidden md:table-cell">{new Date(l.updated_at).toLocaleString("it-IT")}</td>
                    </tr>
                  ))}
                  {!leads.length && <tr><td colSpan={6} className="p-6 text-center text-ink-soft">Nessun cliente ancora</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "payments" && (
            <div className="bg-white border border-border rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-ink-soft"><tr>
                  <th className="text-left p-3 font-medium">Vettura</th><th className="text-left p-3 font-medium">Importo</th>
                  <th className="text-left p-3 font-medium">Stato</th><th className="text-left p-3 font-medium">Data</th>
                </tr></thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-border">
                      <td className="p-3">{p.vehicle_label}</td>
                      <td className="p-3">{euro(p.amount)}</td>
                      <td className="p-3"><span data-testid={`payment-status-${p.id}`} className={`px-2 py-0.5 rounded-full text-xs ${payCls(p.payment_status)}`}>{p.payment_label}</span></td>
                      <td className="p-3 text-ink-soft text-xs">{new Date(p.paid_at || p.created_at).toLocaleString("it-IT")}</td>
                    </tr>
                  ))}
                  {!payments.length && <tr><td colSpan={4} className="p-6 text-center text-ink-soft">Nessun pagamento</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "notifications" && (
            <div className="space-y-2">
              {notes.map((n) => (
                <div key={n.id} className="flex items-center gap-3 p-4 bg-white border border-border rounded-2xl">
                  <Bell size={16} className="text-sand" strokeWidth={1.5} />
                  <div className="flex-1"><div className="text-sm">{n.message}</div>
                    <div className="text-xs text-ink-soft">{new Date(n.created_at).toLocaleString("it-IT")}</div></div>
                </div>
              ))}
              {!notes.length && <div className="p-6 text-center text-ink-soft">Nessuna notifica</div>}
            </div>
          )}

          {tab === "config" && config && (
            <div className="space-y-6">
            <AccountSettings email={user.email} />
            <div className="bg-white border border-border rounded-2xl p-6 space-y-5 max-w-2xl">
              {[
                ["permuta_question", "Domanda permuta"], ["purchase_question", "Domanda modalità acquisto"],
                ["financing_intro", "Titolo finanziamento"], ["block_conditions", "Condizioni di blocco"],
              ].map(([key, label]) => (
                <label key={key} className="block">
                  <span className="text-xs uppercase tracking-wider text-ink-soft">{label}</span>
                  {key === "block_conditions" ? (
                    <textarea data-testid={`config-${key}`} value={config[key] || ""} rows={4}
                      onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
                      className="mt-1.5 w-full p-3 rounded-xl border border-border outline-none focus:border-ink" />
                  ) : (
                    <input data-testid={`config-${key}`} value={config[key] || ""}
                      onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
                      className="mt-1.5 w-full h-12 px-4 rounded-xl border border-border outline-none focus:border-ink" />
                  )}
                </label>
              ))}
              <button data-testid="save-config-btn" onClick={saveConfig} className="px-6 py-3 rounded-full bg-ink text-white text-sm hover:opacity-90 transition-opacity">Salva percorso</button>
            </div>
            </div>
          )}
        </div>
      </main>

      {editing && <VehicleForm vehicle={editing} onClose={() => setEditing(null)} onSave={saveVehicle} />}
      {openLead && <LeadDetail leadId={openLead} onClose={() => setOpenLead(null)} onChanged={() => api.get("/admin/leads").then((r) => setLeads(r.data))} />}
    </div>
  );
}

const DESC_MAX = 4000;
const parseFeatures = (t) => t.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

function VehicleForm({ vehicle, onClose, onSave }) {
  const [d, setD] = useState({
    make: "", model: "", version: "", year: 2022, km: 0, fuel: "Diesel", transmission: "Automatico",
    price: 0, monthly_rate: 0, deposit_amount: 500, leasing_available: false, status: "disponibile",
    description: "", features: [], images: [], ...vehicle,
  });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const [featuresText, setFeaturesText] = useState((vehicle?.features || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); await onSave({ ...d, year: +d.year, km: +d.km, price: +d.price, monthly_rate: +d.monthly_rate, deposit_amount: +d.deposit_amount }); setBusy(false); };

  const I = ({ label, k, type = "text" }) => (
    <label className="block"><span className="text-xs uppercase tracking-wider text-ink-soft">{label}</span>
      <input data-testid={`vf-${k}`} type={type} value={d[k]} onChange={(e) => set(k, e.target.value)}
        className="mt-1.5 w-full h-11 px-3 rounded-xl border border-border outline-none focus:border-ink" /></label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-background w-full md:max-w-2xl rounded-t-3xl md:rounded-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 glass border-b border-border px-6 h-16 flex items-center justify-between">
          <h2 className="font-serif text-2xl">{d.id ? "Modifica vettura" : "Nuova vettura"}</h2>
          <button data-testid="vf-close" onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-secondary"><X size={18} /></button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          <I label="Marca" k="make" /><I label="Modello" k="model" />
          <I label="Versione" k="version" /><I label="Anno" k="year" type="number" />
          <I label="Km" k="km" type="number" /><I label="Alimentazione" k="fuel" />
          <I label="Cambio" k="transmission" /><I label="Prezzo (€)" k="price" type="number" />
          <I label="Rata mensile (€)" k="monthly_rate" type="number" /><I label="Deposito blocco (€)" k="deposit_amount" type="number" />
          <label className="col-span-2 flex items-center gap-3 p-3 rounded-xl border border-border bg-white">
            <input type="checkbox" data-testid="vf-leasing" checked={d.leasing_available} onChange={(e) => set("leasing_available", e.target.checked)} className="w-5 h-5 accent-ink" />
            <span className="text-sm">Leasing disponibile</span>
          </label>
          <label className="block col-span-2">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-ink-soft">Descrizione</span>
              <span data-testid="vf-description-count" className={`text-xs tabular-nums ${d.description.length >= DESC_MAX ? "text-red-600 font-medium" : "text-ink-soft"}`}>{d.description.length.toLocaleString("it-IT")} / {DESC_MAX.toLocaleString("it-IT")}</span>
            </div>
            <textarea data-testid="vf-description" value={d.description} rows={10} maxLength={DESC_MAX} placeholder={"Testo libero su più righe.\nPuoi usare elenchi:\n1. Primo punto\n2. Secondo punto\n- Oppure puntati"}
              onChange={(e) => set("description", e.target.value.slice(0, DESC_MAX))}
              className="mt-1.5 w-full min-h-[240px] p-3 rounded-xl border border-border outline-none focus:border-ink text-base leading-relaxed resize-y whitespace-pre-wrap" /></label>
          <label className="block col-span-2">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-ink-soft">Dotazioni (una per riga o separate da virgola)</span>
              <span data-testid="vf-features-count" className="text-xs text-ink-soft tabular-nums">{d.features.length} accessori</span>
            </div>
            <textarea data-testid="vf-features" value={featuresText} rows={8} placeholder={"Navigatore\nFari LED\nSedili riscaldati\n..."}
              onChange={(e) => { setFeaturesText(e.target.value); set("features", parseFeatures(e.target.value)); }}
              className="mt-1.5 w-full min-h-[180px] p-3 rounded-xl border border-border outline-none focus:border-ink text-base leading-relaxed resize-y" /></label>
          <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-ink-soft">URL immagini (separate da virgola)</span>
            <textarea data-testid="vf-images" value={d.images.join(", ")} rows={2} onChange={(e) => set("images", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
              className="mt-1.5 w-full p-3 rounded-xl border border-border outline-none focus:border-ink" /></label>
        </div>
        <div className="sticky bottom-0 glass border-t border-border p-4">
          <button data-testid="vf-save" onClick={submit} disabled={busy}
            className="w-full h-13 py-3.5 rounded-xl bg-ink text-white flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Salva vettura"}
          </button>
        </div>
      </div>
    </div>
  );
}
